import reduxApi from 'redux-api';
import { adapterAxios } from '~/utils/redux-api';
import {
  merge,
  set,
  get,
  words,
  lowerFirst,
  flow,
  mapKeys,
  snakeCase,
} from 'lodash';

// defines the base url for the v2 api
export const baseUrl = '/api/v2';

// the default options for fetch
export const defaultOptions = {
  credentials: 'same-origin',
  headers: {
    Accept: 'application/json',
  },
};

interface Endpoint extends Record<string, unknown> {
  options?: Record<string, unknown>;
}

interface Api {
  events: Record<string, object>;
  reducers: Record<string, object>;
  transformers?: Record<string, (data: unknown) => unknown>;
}

interface ActionData<T> {
  data: T;
}

// merges the default options into the given endpoint
//
// endpoint.options has highest priority
// followed by the overall options
// then the default options
export const mergeEndpointOptions = (
  endpoint: Endpoint,
  options: Record<string, unknown>,
  baseUrl: string
) => ({
  ...endpoint,
  ...merge(
    {
      url: `${baseUrl}/${get(endpoint, 'url')}`,
    },
    { options },
    { options: endpoint.options }
  ),
});

// converts a basic endpoints object into one which can be processed by redux-api
export const createEndpoint = (
  endpoints: Record<string, Endpoint>,
  options: Record<string, unknown> = {},
  baseUrl: string
) =>
  Object.keys(endpoints).reduce(
    (result, key) =>
      set(
        result,
        key,
        mergeEndpointOptions(get(endpoints, key), options, baseUrl)
      ),
    {}
  );

const defaultTransformer = <T>(res: T) => res;

function withBaseActions(api: Api) {
  const { events, reducers, transformers } = api;

  const endpoints = Object.keys(reducers);

  endpoints.forEach((endpointKey) => {
    const actionKeys = Object.keys(get(events, endpointKey));
    const fns = actionKeys.reduce((actions, key) => {
      const actionName = lowerFirst(words(key).slice(1).join());

      function action(
        actionData: ActionData<unknown>,
        transform: boolean = true
      ) {
        const transformer = transform
          ? get(transformers, endpointKey, defaultTransformer)
          : defaultTransformer;

        return {
          type: get(events, [endpointKey, key]),
          ...actionData,
          data: transformer(actionData.data),
        };
      }

      return set(actions, actionName, action);
    }, {});

    set(api, ['baseActions', endpointKey], fns);
  });

  return api;
}

function withTransformers(api: Api, endpoints: Record<string, Endpoint>) {
  const transformers = Object.keys(endpoints).reduce((transformers, key) => {
    const transformer = get(endpoints, [key, 'transformer']);
    if (transformer != null) {
      return { ...transformers, [key]: transformer };
    }

    return transformers;
  }, {});

  return { transformers, ...api };
}

const reduxApiWithFetch = (
  endpoints: Record<string, Endpoint>,
  options: Record<string, unknown> = {}
) => {
  const api = reduxApi(endpoints, options)
    .use('fetch', adapterAxios())
    .use('options', () => defaultOptions);

  return withTransformers(api, endpoints);
};

// returns a redux-api for the given endpoints object
export const createApi = flow(reduxApiWithFetch, withBaseActions);

/** Convert an objects' keys to snake case */
export function keysToSnakeCase<Obj extends {}>(obj: Obj) {
  return mapKeys(obj, (_, key) => snakeCase(key));
}

export const sanitizeString = (value: string) => {
  return value
    .split('\n')
    .map((line) => line.trim())
    .join('\n');
};
